import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { SalesCampaign } from "@xop/dashboard-shared";

import { useCan } from "@/auth/permissions";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { PageLoader } from "@/components/ui/page-loader";
import { salesApi } from "../client.js";
import { CampaignEditor } from "./CampaignEditor.js";
import { CampaignReport } from "./CampaignReport.js";
import { formatWindow, StatusChip } from "./campaignStatus.js";

/**
 * Sales ▸ Campaigns — operator-defined, time-boxed harvest targets.
 *
 * Why this page exists: the harvester's verticals used to be hardcoded in the
 * agent's Python, so a sector it didn't already know could not be targeted.
 * Steering was the only lever, and it matches a fixed vocabulary and fails
 * OPEN — a note reading "hunt ONLY banking, trading" matched nothing, became
 * an empty (= unconstrained) constraint, and the sweep carried on through oil
 * refineries while the instruction appeared to be ignored.
 *
 * So the single most important thing on this page is the "sweeping now"
 * banner. It reports what the harvester is ACTUALLY working — live campaigns,
 * or the default catalog — rather than letting a draft campaign sit on screen
 * and imply it is running. That gap is exactly what cost a week.
 */
export function CampaignsPage(): JSX.Element {
  const canEdit = useCan("sales:edit");
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SalesCampaign | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<SalesCampaign | null>(null);
  const [openReport, setOpenReport] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ["sales", "campaigns"],
    queryFn: () => salesApi.getCampaigns(),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["sales", "campaigns"] });
  };

  const setStatus = useMutation({
    mutationFn: ({ slug, status }: { slug: string; status: SalesCampaign["status"] }) =>
      salesApi.updateCampaign(slug, { status }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (slug: string) => salesApi.deleteCampaign(slug),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
  });

  if (isPending) return <PageLoader />;
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Couldn’t load campaigns. {(error as Error).message}
      </Alert>
    );
  }

  const campaigns = data?.campaigns ?? [];
  const live = campaigns.filter((c) => c.is_live);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Campaigns"
        description="Time-boxed harvest targets. Activate one and the harvester sweeps only its verticals."
        actions={
          canEdit ? (
            <Button variant="contained" onClick={() => setCreating(true)}>
              New campaign
            </Button>
          ) : undefined
        }
      />

      {/* The honest answer to "what is it doing right now". */}
      <Alert
        severity={data?.using_default_catalog ? "warning" : "success"}
        sx={{ mb: 3 }}
        data-testid="sweeping-banner"
      >
        {data?.using_default_catalog ? (
          <>
            <strong>No campaign is live — sweeping the default catalog</strong> (
            nuclear, oil, gas, power, chemical, aviation, healthcare, public
            safety, defence). Create a campaign and set it <em>Active</em> to
            target something else. A steer alone cannot: it matches a fixed
            vocabulary and silently does nothing when it doesn’t recognise a word.
          </>
        ) : (
          <>
            <strong>Sweeping now:</strong>{" "}
            {live.map((c) => c.label).join(", ")} — nothing else is being
            harvested.
          </>
        )}
      </Alert>

      {campaigns.length === 0 ? (
        <EmptyState
          title="No campaigns yet"
          description="A campaign is a month’s target: the sectors to hunt, where, and until when. Create one to point the harvester at a sector the default catalog doesn’t cover."
        />
      ) : (
        <Stack spacing={2}>
          {campaigns.map((c) => (
            <Card key={c.id} data-testid={`campaign-${c.slug}`}>
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography sx={{ fontWeight: 600 }}>{c.label}</Typography>
                      <StatusChip campaign={c} />
                    </Stack>
                    {c.goal && (
                      <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
                        {c.goal}
                      </Typography>
                    )}
                    <Typography
                      sx={{ color: "text.disabled", fontSize: 12, mt: 0.5 }}
                    >
                      {formatWindow(c)} · {c.verticals.length} vertical
                      {c.verticals.length === 1 ? "" : "s"} ·{" "}
                      {c.regions.length === 0
                        ? "worldwide"
                        : `${c.regions.length} region${c.regions.length === 1 ? "" : "s"}`}
                    </Typography>
                    <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                      {c.verticals.map((v) => (
                        <Chip key={v.vertical} label={v.label || v.vertical} size="small" />
                      ))}
                    </Stack>
                  </Box>

                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Button
                      size="small"
                      onClick={() =>
                        setOpenReport(openReport === c.slug ? null : c.slug)
                      }
                    >
                      {openReport === c.slug ? "Hide results" : "Results"}
                    </Button>
                    {/* Every company + contact this campaign found, and the
                        "Create contact group" hand-off to Email Campaigns. */}
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/sales/campaigns/${encodeURIComponent(c.slug)}`}
                    >
                      Companies &amp; contacts
                    </Button>
                    {canEdit && (
                      <>
                        <Button size="small" onClick={() => setEditing(c)}>
                          Edit
                        </Button>
                        {c.status === "active" ? (
                          <Button
                            size="small"
                            onClick={() =>
                              setStatus.mutate({ slug: c.slug, status: "archived" })
                            }
                          >
                            Archive
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setStatus.mutate({ slug: c.slug, status: "active" })
                            }
                          >
                            Activate
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setDeleting(c)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </Stack>
                </Stack>

                {openReport === c.slug && (
                  <Box sx={{ mt: 2 }}>
                    <CampaignReport slug={c.slug} />
                    <Button
                      sx={{ mt: 1.5 }}
                      component={RouterLink}
                      to={`/sales/campaigns/${encodeURIComponent(c.slug)}`}
                    >
                      See every company and contact →
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {(creating || editing) && (
        <CampaignEditor
          campaign={editing}
          availableRegions={data?.available_regions ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            invalidate();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        title={`Delete “${deleting?.label ?? ""}”?`}
        // The reassurance matters: an operator will hesitate to delete a
        // campaign if they think it takes the contacts with it.
        description="This removes the campaign definition only. Every organisation and contact it harvested is kept and stays searchable, and its results page keeps working."
        confirmLabel="Delete campaign"
        variant="destructive"
        onConfirm={async () => {
          if (deleting) await remove.mutateAsync(deleting.slug);
        }}
        onClose={() => setDeleting(null)}
      />
    </Box>
  );
}
